export const RENT_BOUNDS = { min: 400, max: 20000 } as const;
export const AREA_BOUNDS = { min: 20, max: 1000 } as const;
export const DEFAULT_RENT_RANGE: [number, number] = [
  RENT_BOUNDS.min,
  RENT_BOUNDS.max,
];
export const DEFAULT_AREA_RANGE: [number, number] = [
  AREA_BOUNDS.min,
  AREA_BOUNDS.max,
];

export function clampValue(value: number, [min, max]: [number, number]) {
  return value < min ? min : value > max ? max : value;
}

export function parseBoundedNumericInput(
  raw: string,
  fallback: number,
  bounds: { min: number; max: number }
): number {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return fallback;
  }

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) {
    return fallback;
  }

  return clampValue(parsed, [bounds.min, bounds.max]);
}

export function parseNumericInputOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return parsed;
}

export function isInvertedRangeInput(
  minInput: string,
  maxInput: string,
  bounds: { min: number; max: number }
): boolean {
  const minParsed = parseNumericInputOrNull(minInput);
  const maxParsed = parseNumericInputOrNull(maxInput);

  if (minParsed === null || maxParsed === null) {
    return false;
  }

  // Compare raw parsed numbers so that "max < min" is always detected,
  // even when values fall outside the global bounds.
  return minParsed > maxParsed;
}

export function normalizeRange(
  min: number,
  max: number,
  bounds: { min: number; max: number }
): [number, number] {
  const clampedMin = clampValue(min, [bounds.min, bounds.max]);
  const clampedMax = clampValue(max, [bounds.min, bounds.max]);
  return clampedMin <= clampedMax ? [clampedMin, clampedMax] : [clampedMin, clampedMin];
}

export function parseInputForSliderSync(
  raw: string,
  fallback: number,
  bounds: { min: number; max: number }
): number {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return fallback;
  }

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) {
    return fallback;
  }

  return clampValue(parsed, [bounds.min, bounds.max]);
}

export function getRentRangeForSlider(
  rentMinInput: string,
  rentMaxInput: string,
  currentRange: [number, number]
): [number, number] {
  if (isInvertedRangeInput(rentMinInput, rentMaxInput, RENT_BOUNDS)) {
    return currentRange;
  }

  const min = parseInputForSliderSync(
    rentMinInput,
    currentRange[0],
    RENT_BOUNDS
  );
  const max = parseInputForSliderSync(
    rentMaxInput,
    currentRange[1],
    RENT_BOUNDS
  );
  return normalizeRange(min, max, RENT_BOUNDS);
}

export function getAreaRangeForSlider(
  areaMinInput: string,
  areaMaxInput: string,
  currentRange: [number, number]
): [number, number] {
  if (isInvertedRangeInput(areaMinInput, areaMaxInput, AREA_BOUNDS)) {
    return currentRange;
  }

  const min = parseInputForSliderSync(areaMinInput, currentRange[0], AREA_BOUNDS);
  const max = parseInputForSliderSync(areaMaxInput, currentRange[1], AREA_BOUNDS);
  return normalizeRange(min, max, AREA_BOUNDS);
}

export function resolveRentRangeFromInputs(
  rentMinInput: string,
  rentMaxInput: string
): [number, number] {
  const rentMin = parseBoundedNumericInput(
    rentMinInput,
    RENT_BOUNDS.min,
    RENT_BOUNDS
  );
  const rentMax = parseBoundedNumericInput(
    rentMaxInput,
    RENT_BOUNDS.max,
    RENT_BOUNDS
  );
  return normalizeRange(rentMin, rentMax, RENT_BOUNDS);
}

export function resolveAreaRangeFromInputs(
  areaMinInput: string,
  areaMaxInput: string
): [number, number] {
  const areaMin = parseBoundedNumericInput(
    areaMinInput,
    AREA_BOUNDS.min,
    AREA_BOUNDS
  );
  const areaMax = parseBoundedNumericInput(
    areaMaxInput,
    AREA_BOUNDS.max,
    AREA_BOUNDS
  );
  return normalizeRange(areaMin, areaMax, AREA_BOUNDS);
}
